<script>
import $ from 'domtastic';
import eventHub from 'viralstyle-ui/src/eventHub';
import vueMixins from 'viralstyle-ui/src/vueMixins';
import scrollAnimator from 'viralstyle-ui/src/scrollAnimator';
import VsIconButton from 'viralstyle-ui/src/VsIconButton';
import VsToaster from 'viralstyle-ui/src/VsToaster';
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';

import navigationHelper from 'utils/navigation-helper';
import clientMixins from 'utils/client-mixins';
import Impersonation from 'components/Impersonation';
import LeftNavigation from 'components/LeftNavigation';
import Logo from 'components/Logo';
import Login from 'components/Login';
import TopNavigation from 'components/TopNavigation';
import { privileges } from 'domains/base/_base-enum';

Vue.mixin({
  methods: {
    updateState(object, property, value) {
      this.$store.commit('updateState', { object, property, value });
    },
  },
});

export default {
  components: {
    VsIconButton,
    Logo,
    TopNavigation,
    LeftNavigation,
    VsToaster,
    Login,
    Impersonation,
  },
  mixins: [vueMixins, clientMixins],
  data: () => ({
    intercomInitialized: false,
    showScrollToTop: false,
  }),
  computed: {
    ...mapState([
      'notifications',
      'loginVisible',
      'navMenuOpen',
      'ready',
      'currentUser',
      'intercomId',
      'intercomHash',
      'currentUserEmail',
      'currentUserId',
      'currentUserRoleInfo',
      'isImpersonation',
    ]),
    ...mapGetters(['permissionAccessDomains']),
  },
  watch: {
    loginVisible(val) {
      let intercomEl = document.getElementById('intercom-container');

      if (val) {
        this.$_isNotReady();
        if (intercomEl) intercomEl.style.display = 'none';
      } else {
        this.$_init();
        if (intercomEl) intercomEl.style.display = 'block';
      }
    },
  },
  created() {
    this.$_init();
    $(window).on('scroll', this.$_scrollFunc);
  },
  beforeDestroy() {
    $(window).off('scroll', this.$_scrollFunc);
  },
  methods: {
    async $_init() {
      await this.$store.dispatch('getApiRoutes');

      await this.$store.dispatch('getCurrentUserId');

      if (this.hasWhitelabelDomain && this.permissionAccessDomains) {
        await this.$store.dispatch('getWhitelabelConfigs');
      }

      this.$store.dispatch('getMyOrders')

      this.$store.commit('applicationReady', true);

      window.dataLayer.push({ event: 'Load Tags' });

      // this is to make sure permissions are propagated
      navigationHelper.routeChanged(this.$route, '', this.currentUserRoleInfo);

      if (!this.intercomInitialized) {
        let isBaseUser = this.currentUserId === this.currentUser.id;

        this.$intercom.boot({
          user_id: this.intercomId,
          name: isBaseUser ? this.currentUser.name : '',
          email: this.currentUserEmail,
          created_at: isBaseUser ? this.currentUser.created_at : '',
          user_hash: this.intercomHash,
          hide_default_launcher: true,
        });
        this.intercomInitialized = true;
      }
    },
    $_isNotReady() {
      this.$store.commit('applicationReady', false);
    },
    $_scrollFunc() {
      this.showScrollToTop = this.scrollTop() > 0;
    },
    handleEscPressed() {
      eventHub.$emit('esc-pressed');
    },
    handleRemoveNotification(notification) {
      this.$store.commit('removeNotification', notification);
    },
    handleScrollToTop() {
      scrollAnimator.scrollToTop();
    },
  },
};
</script>

<template lang='pug'>
body.app(:class='{ "no-scroll--mobile": navMenuOpen }', @keyup.esc='handleEscPressed')
  logo
  top-navigation(:ready='ready')
  transition(name='left-nav')
    left-navigation(v-if='ready')
  #content
    router-view(v-if='ready')
    transition(name='scroll-to-top')
      vs-icon-button.scroll-to-top(v-show='showScrollToTop', icon='chevron-up', @click='handleScrollToTop')
  vs-toaster(:notifications='notifications', @remove-toast='handleRemoveNotification')
  portal-target(name='body')
  login(:ready='ready')
  impersonation(v-if='isImpersonation')
  #google_recaptcha(v-show='loginVisible')
</template>

<style>
@import '~import/index';
@import '~styles/index';

.app {
  @extend %flex;
  @extend %flex--wrap;
}

.no-scroll {
  overflow: hidden;
  -webkit-overflow-scrolling: auto;

  &--mobile {
    overflow: hidden;
    -webkit-overflow-scrolling: auto;

    @include mq(mf-md) {
      overflow: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
}

#content {
  @extend %full-width;
  background-color: $backgroundColor;
  order: 4;
  padding: 0 .5rem;
  position: relative;
  min-height: calc(100vh - 51px);

  @include mq(mf-md) {
    width: calc(100% - 130px);
    min-height: calc(100vh - 100px);
    padding: 0;
  }
}

#google_recaptcha {
  z-index: 9999;
}

.scroll-to-top {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 1;
  box-shadow: 0px 0px 12.5px rgba(0, 0, 0, .35);

  @include mq(mf-xl) {
    right: 2rem;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &-enter-active,
  &-leave-active {
    transition: opacity 300ms;
  }

  &-enter-to,
  &-leave {
    opacity: 1;
  }
}

.vs-section {
  @extend %flex;
  @extend %flex--wrap;
  @extend %flex--middle;
}

.vs-container {
  @extend %content-container;
  @extend %flex;
  @extend %flex--center;
  @extend %flex--wrap;

  &--settings,
  &--settings-small {
    @extend %settings-container;
    @extend %full-width;
  }

  &--settings {
    max-width: 1400px;
  }

  &--settings-small {
    max-width: 1000px;
  }

  &--content,
  &__table--content {
    @extend %content-margin;
  }

  &__heading {
    @extend %content-heading;
    @include col(xs, 12);
    padding: 2rem;

    &--button {
      @extend %flex;
      @extend %flex--space-between;
      @extend %flex--middle;
      padding: 1.146rem 2rem
    }
  }

  &__padding {
    @include mq(mf-md) {
      padding: 0 1.5rem;
    }
  }

  &__content {
    border-top: 1px solid $borderColor;
    padding: 0 1rem;
  }
}

.form {
  @extend %flex;
  @extend %flex--wrap;
  @extend %full-width;
  padding: 2rem;

  &--small {
    @include col(xs, 12);
    @include col(sm, 12);
    @include col(md, 6);
  }

  &--center {
    @extend %flex--middle;
  }

  &--large-gutter {
    >[class^="form"] {
      padding: 1.5rem;
    }
  }

  &--no-padding {
    &-top {
      padding-top: 0;
    }

    &-bottom {
      padding-bottom: 0;
    }
  }

  &__text {
    margin: 1rem 0 0;
  }

  &__col {
    &--right {
      @extend %text-right;
    }

    &--lg-left {
      @include mq(mf-lg) {
        text-align: left;
      }
    }

    &--bottom {
      align-self: flex-end;
    }
  }

  &__one,
  &__two,
  &__three,
  &__four,
  &__five,
  &__half,
  &__seven,
  &__eight,
  &__nine,
  &__ten,
  &__eleven,
  &__full {
    @include col(xs, 12);
    margin-bottom: 2rem;
  }

  &__two {
    @include col(lg, 2);

    &--mobile {
      @include col(xs, 2);
    }
  }

  &__three {
    @include col(xs, 6);
    @include col(lg, 3);
  }

  &__four {
    @include col(lg, 4);
  }

  &__five {
    @include col(lg, 5);
  }

  &__half {
    @include col(lg, 6);
  }

  &__seven {
    @include col(lg, 7);
  }

  &__eight {
    @include col(lg, 8);
  }

  &__ten {
    @include col(lg, 10);

    &--mobile {
      @include col(xs, 10);
    }
  }
}

.accordion-content {
  padding: 2rem;
  text-align: right;

  @include mq(mf-md) {
    padding: 4rem;
  }
}

.accordion-content__client {
  text-align: right;

  .vs-input,
  .vs-form-option__group,
  .vs-error,
  .vs-table,
  .vs-button-toggle {
    margin-bottom: 2rem;
  }

  .vs-error {
    @extend %text-left;
  }
}

textarea {
  resize: none;
  min-height: 150px;
}

.dz {

  &-details,
  &-error-mark,
  &-error-message,
  &-progress,
  &-success-mark {
    display: none;
  }

  &-image {
    >img {
      @extend %oversize-center;
    }
  }
}

.v-touch {
  @extend %flex;
  @include col(xs, 12);
}

.copy-input {
  width: 1px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: transparent;

  &::selection {
    background: transparent;
  }
}

.dashboard {
  &__container {
    padding: 0 0;

    @include mq(mf-md) {
      padding: 0 1rem;
    }

    @include mq(mf-lg) {
      padding: 0 2rem;
    }

    @include mq(mf-xl) {
      padding: 0 6rem;
    }
  }

  &-charts {
    @extend %flex;
    @extend %flex--wrap;
    @include col(xs, 12);
    padding-left: 0;
    padding-right: 0;
    margin-top: 2rem;
  }
}

.get-started {
  color: $textAccentColor;
  line-height: 1.75;
  margin-top: 4rem;
  margin-bottom: 4rem;

  &__icon {
    line-height: 1;
    font-size: 16rem;

    @include mq(mf-md) {
      margin-right: 2rem;
    }
  }

  &__instructions {
    @extend %text-center;

    @include mq(mf-sm) {
      text-align: left;
    }
  }

  h6 {
    margin-top: 1rem;

    &.get-started--no-margin {
      margin-top: 0;
    }
  }
}

.ghost-submit {
  display: none;
}

.content-loader {
  position: absolute;
  top: 10rem;
  left: 50%;
  transform: translateX(-50%);
  height: 8rem;
}

.absolute {
  &--center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &-vertical {
      top: 50%;
      transform: translateY(-50%);
    }

    &-horizontal {
      left: 50%;
      transform: translateX(-50%);
    }
  }
}

.break-anywhere {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.content-margin {
  @extend %content-margin;

  .vs-table__title-container {
    @apply border-b border-border border-solid;
  }
}

img,
.vs-product-slider__image,
.details-upsell__image,
.campaign__image {
  opacity: 0;
  transition: opacity 500ms;
}

img[lazy=loading],
.vs-product-slider__image[lazy=loading],
.details-upsell__image[lazy=loading],
.campaign__image[lazy=loading],
img[lazy=loaded],
.vs-product-slider__image[lazy=loaded],
.details-upsell__image[lazy=loaded],
.campaign__image[lazy=loaded],
.ql-snow .ql-editor img,
.dz-image img {
  opacity: 1;
}

.rounded-xl {
  border-radius: 1rem !important;
}
</style>
